/**
 * Initialize the undo functionality for the canvas.
 *
 * @param {Object} dom - The DOM elements for the undo module.
 * @param {Function} checkCanvasHasContent - Function to check if the canvas has content.
 * @param {Function} resetToDrawingMode - Function to reset the UI to drawing mode.
 *
 * @returns {void}
 */
export default function initUndo({ dom, checkCanvasHasContent, resetToDrawingMode }) {
	const { canvas, accessoryContainer, undoButton, clearButton } = dom;
	const ctx = canvas.getContext('2d');
	const undoStack = [];
	const maxUndoSteps = 20;

	/**
	 * Push the current canvas and accessory state onto the undo stack.
	 *
	 * @returns {void}
	 */
	const pushState = () => {
		if (undoStack.length >= maxUndoSteps) undoStack.shift();

		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const accessories = Array.from(
			accessoryContainer.querySelectorAll('img[data-placed-accessory]'),
		).map((img) => ({
			src: img.src,
			left: img.style.left,
			top: img.style.top,
			width: img.style.width,
			flipped: img.classList.contains('flipped'),
		}));
		undoStack.push({ imageData, accessories });
	};

	/**
	 *  Undo the last action
	 *
	 * @returns {void}
	 */
	const undo = () => {
		if (!undoStack.length) return;
		const { imageData, accessories } = undoStack.pop();

		ctx.putImageData(imageData, 0, 0);
		accessoryContainer.innerHTML = '';

		accessories.forEach((acc) => {
			const imgEl = new Image();
			imgEl.src = acc.src;
			imgEl.className = 'wp-block-mavis-design-a-hairstyle__canvas-accessory';
			if (acc.flipped) imgEl.classList.add('flipped');
			imgEl.setAttribute('data-placed-accessory', 'true');
			Object.assign(imgEl.style, {
				position: 'absolute',
				width: acc.width,
				left: acc.left,
				top: acc.top,
				zIndex: '3',
			});
			accessoryContainer.appendChild(imgEl);
		});

		if (!checkCanvasHasContent()) {
			resetToDrawingMode();
		}
	};

	if (undoButton) {
		undoButton.addEventListener('click', (e) => {
			e.preventDefault();
			undo();
		});
	}

	if (clearButton) {
		clearButton.addEventListener('click', () => {
			pushState(); // Save state before clearing
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			accessoryContainer.innerHTML = '';
			if (!checkCanvasHasContent()) {
				resetToDrawingMode();
			}
		});
	}

	document.addEventListener('keydown', (e) => {
		if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
			e.preventDefault();
			undo();
		}
	});

	return { pushState, undo };
}
